import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import clsx from 'clsx';
import Image from 'next/image';

type DrawerProps = {
  className?: string;
  title?: string;
  description?: string;
  children: React.ReactNode;
  submenu: React.ReactNode;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  submenuTitle: string;
  isSubMenuOpen: boolean;
  setSubMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function Drawer({
  className,
  title = '',
  description = '',
  children,
  submenu,
  isOpen,
  setIsOpen,
  submenuTitle,
  isSubMenuOpen,
  setSubMenuOpen,
}: DrawerProps) {
  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog
        unmount={false}
        onClose={() => {
          setIsOpen(false);
          setSubMenuOpen(false);
        }}
        className="fixed inset-0 overflow-y-auto z-[99]"
      >
        <div className={clsx(className, "flex w-3/4")}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-in duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-30"
            entered="opacity-30"
            leave="transition-opacity ease-out duration-300"
            leaveFrom="opacity-30"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="z-10 fixed inset-0 bg-onyx cursor-pointer" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <div
              className={clsx(
                "flex flex-col justify-between bg-onyx z-50 w-full max-w-sm p-6 overflow-hidden text-left align-middle shadow-xl text-white",
                { hidden: isSubMenuOpen }
              )}
            >
              <div className="h-full">
                <Dialog.Title className="font-bold text-2xl md:text-4xl">
                  {title}
                </Dialog.Title>
                <Dialog.Description>{description}</Dialog.Description>
                {!isSubMenuOpen && children}
              </div>
            </div>
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <div
              className={clsx(
                "flex flex-col justify-between bg-onyx z-50 w-full max-w-sm p-6 overflow-hidden text-left align-middle shadow-xl",
                { hidden: !isSubMenuOpen }
              )}
            >
              <div className="h-full text-white">
                <div className="flex mb-2">
                  <div className="flex-grow">
                    <button onClick={() => setSubMenuOpen(false)} className="text-white">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 12 10"
                        fill="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                        className="fill-current"
                      >
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M5.79488 9.69458C5.52151 9.96795 5.0783 9.96795 4.80493 9.69458L0.604928 5.49458C0.33156 5.22122 0.33156 4.778 0.604928 4.50463L4.80493 0.304635C5.0783 0.0312675 5.52151 0.0312675 5.79488 0.304635C6.06824 0.578002 6.06824 1.02122 5.79488 1.29458L2.78985 4.29961H10.8999C11.2865 4.29961 11.5999 4.61301 11.5999 4.99961C11.5999 5.38621 11.2865 5.69961 10.8999 5.69961L2.78985 5.69961L5.79488 8.70463C6.06824 8.978 6.06824 9.42122 5.79488 9.69458Z"
                        />
                      </svg>
                    </button>
                  </div>
                  <Dialog.Title className="text-xl font-sans font-thin italic uppercase text-[#969D9D]">
                    {submenuTitle}
                  </Dialog.Title>
                </div>
                <hr className="border-white border-opacity-50 mb-2" />
                {submenu}
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
